import React, { useCallback, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCalendarDays,
  faCheckCircle,
  faCircleExclamation,
  faClockRotateLeft,
  faDownload,
  faFilter,
  faGear,
  faRotate,
  faScaleBalanced,
  faShieldHalved,
  faWallet,
} from "@fortawesome/free-solid-svg-icons";
import { adminFluxopayApi } from "../../services/fluxopayApi";
import ReconciliationDetailsModal from "../components/ReconciliationDetailsModal";
import TransactionProofModal from "../components/TransactionProofModal";
import FluxopayDisputesPanel from "../../components/fluxopay/FluxopayDisputesPanel";
import "./AdminFluxopayPage.css";

import AdminFluxopayProviderFees from "../components/AdminFluxopayProviderFees";
import AdminFluxopayRealOverviewV2 from "../components/AdminFluxopayRealOverviewV2";

import AdminFluxopayPayoutReviews from "../components/AdminFluxopayPayoutReviews";

import AdminFluxopayOperationalAlerts from "../components/AdminFluxopayOperationalAlerts";
import AdminFluxopayWebhookHealth from "../components/AdminFluxopayWebhookHealth";

import {fluxopayStatus,} from "../../components/fluxopay/fluxopayStatus";
import AdminFluxopayRiskPolicyModal from "../components/AdminFluxopayRiskPolicyModal";
const brl = (value) =>
  Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const pct = (value) =>
  `${Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}%`;

let CLINICS = [];

let TRANSACTIONS = [];

let RECONCILIATIONS = [];

function Badge({ status }) {
  const statusInfo = fluxopayStatus(status);

  return (
    <span className={`afxp-badge afxp-badge--${statusInfo.tone}`}>
      <i />
      {statusInfo.label}
    </span>
  );
}

function Stat({ icon, label, value, detail, tone = "blue" }) {
  return (
    <article className="afxp-stat">
      <span className={`afxp-stat__icon ${tone}`}>
        <FontAwesomeIcon icon={icon} />
      </span>
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
        <p>{detail}</p>
      </div>
    </article>
  );
}

let FEE_RULES = [];
const fromCents = (value) => Number(value || 0) / 100;
const prettyDate = (value) =>
  value ? new Date(value).toLocaleString("pt-BR") : "—";

function hydrateAdmin(
  accounts,
  transactions,
  feeRules,
  reconciliations,
) {

  FEE_RULES = feeRules.data || [];
  CLINICS = (accounts.data || []).map((account) => ({
    accountId: account.id,
    id: account.entidade_id,
    entity: account.entidade,
    name: account.entidade?.nome || `Entidade #${account.entidade_id}`,
    provider: account.provider,
    status: account.status,
    onboarding: account.provider_payload?.onboarding_request || {},
    volume: fromCents(account.gross_volume_cents),
    transactions: account.payment_orders_count || 0,
    platformRevenue: 0,
    available: fromCents(
      account.provider === "asaas"
        ? account.provider_available_balance_cents
        : account.available_balance_cents,
    ),
    disputes: 0,
  }));
  TRANSACTIONS = (transactions.data || []).map((transaction) => {
    const order = transaction.order || {};
    return {
      transactionId: transaction.id,
      id:
        order.external_id ||
        transaction.provider_transaction_id ||
        `FXP-${transaction.id}`,
      clinic:
        transaction.entidade?.nome || `Entidade #${transaction.entidade_id}`,
      patient: order.paciente?.nome || "Paciente não informado",
      method:
        order.payment_method === "pix"
          ? "Pix"
          : `Cartão · ${order.installments || 1}x`,
      gross: fromCents(order.gross_amount_cents || transaction.amount_cents),
      providerFee: fromCents(order.provider_fee_cents),
      platformFee: fromCents(order.platform_fee_cents),
      net: fromCents(order.clinic_net_cents),
      status: transaction.status,
      date: prettyDate(transaction.created_at),
    };
  });
  RECONCILIATIONS = (reconciliations.data || []).map((run) => ({
    id: run.id,
    date: new Date(
      `${String(run.reference_date).slice(0, 10)}T12:00:00`,
    ).toLocaleDateString("pt-BR"),
    provider: run.provider,
    internal: fromCents(run.internal_total_cents),
    providerAmount: fromCents(run.provider_total_cents),
    difference: fromCents(run.difference_cents),
    status: run.status,
    details: run.summary?.accounts_failed
      ? `${run.summary.accounts_failed} subconta(s) com erro de conexão`
      : `${run.matched_count || 0} cobrança(s) conciliada(s)`,
  }));
}

function Clinics({ onChanged, setError }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [riskAccount, setRiskAccount] = useState(null);
  const [saving, setSaving] = useState(false);
  const rows = CLINICS.filter((c) =>
    c.name.toLowerCase().includes(query.toLowerCase()),
  );
  const onboard = async () => {
    if (!selected) return;
    setSaving(true);
    setError("");
    try {
      await adminFluxopayApi.validateAsaasAccount(
        selected.id,
        selected.onboarding,
      );
      await adminFluxopayApi.onboardAsaasAccount(
        selected.id,
        selected.onboarding,
      );
      setSelected(null);
      await onChanged();
    } catch (e) {
      setError(
        e.response?.data?.message || "Não foi possível criar a subconta Asaas.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <>
      <section className="afxp-card afxp-table-card">
        <header>
          <div>
            <small>SUBCONTAS</small>
            <h3>Operação por clínica</h3>
          </div>
          <button className="afxp-secondary">
            <FontAwesomeIcon icon={faDownload} />
            Exportar
          </button>
        </header>
        <div className="afxp-filters">
          <label>
            <FontAwesomeIcon icon={faFilter} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar clínica"
            />
          </label>
          <select>
            <option>Todos os provedores</option>
            <option>Asaas</option>
          </select>
          <select>
            <option>Todos os status</option>
            <option>Solicitação pendente</option>
            <option>Ativa</option>
            <option>Em análise</option>
            <option>Bloqueada</option>
          </select>
        </div>
        <div className="afxp-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Clínica</th>
                <th>Provedor</th>
                <th>TPV</th>
                <th>Transações</th>
                <th>Saldo disponível</th>
                <th>Status</th>
                <th>Ação</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.accountId}>
                  <td>
                    <b>{c.name}</b>
                    <small>Entidade #{c.id}</small>
                  </td>
                  <td>
                    <span className="afxp-provider">{c.provider}</span>
                  </td>
                  <td>
                    <b>{brl(c.volume)}</b>
                  </td>
                  <td>{c.transactions}</td>
                  <td>{brl(c.available)}</td>
                  <td>
                    <Badge status={c.status} />
                  </td>
                  <td>
                    <div className="afxp-row-actions">
                      {c.status === "pending_review" && (
                        <button
                          className="afxp-primary"
                          onClick={() => setSelected(c)}
                        >
                          Analisar solicitação
                        </button>
                      )}

                      <button
                        type="button"
                        className="afxp-secondary"
                        onClick={() => setRiskAccount(c)}
                      >
                        Política de risco
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {selected && (
        <div
          className="afxp-modal-backdrop"
          onMouseDown={() => setSelected(null)}
        >
          <section
            className="afxp-modal"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <header>
              <div>
                <small>ONBOARDING ASAAS</small>
                <h2>{selected.name}</h2>
              </div>
              <button onClick={() => setSelected(null)}>×</button>
            </header>
            <div className="afxp-review-grid">
              <span>
                CNPJ<b>{selected.entity?.cnpj || "Não informado"}</b>
              </span>
              <span>
                E-mail<b>{selected.onboarding.email}</b>
              </span>
              <span>
                Telefone<b>{selected.onboarding.mobile_phone}</b>
              </span>
              <span>
                Faturamento mensal<b>{brl(selected.onboarding.income_value)}</b>
              </span>
              <span>
                Tipo empresarial<b>{selected.onboarding.company_type}</b>
              </span>
              <span>
                Endereço
                <b>
                  {selected.onboarding.address},{" "}
                  {selected.onboarding.address_number}
                </b>
              </span>
              <span>
                Bairro<b>{selected.onboarding.province}</b>
              </span>
              <span>
                CEP<b>{selected.onboarding.postal_code}</b>
              </span>
            </div>
            <p className="afxp-warning">
              <FontAwesomeIcon icon={faCircleExclamation} /> Esta ação criará
              uma subconta real no Sandbox Asaas e armazenará sua credencial
              criptografada.
            </p>
            <footer>
              <button
                className="afxp-secondary"
                onClick={() => setSelected(null)}
              >
                Cancelar
              </button>
              <button
                className="afxp-primary"
                onClick={onboard}
                disabled={saving}
              >
                {saving ? "Criando subconta..." : "Aprovar e criar no Asaas"}
              </button>
            </footer>
          </section>
        </div>
      )}

      {riskAccount && (
        <AdminFluxopayRiskPolicyModal
          accountId={riskAccount.accountId}
          onClose={() => setRiskAccount(null)}
          onSaved={onChanged}
        />
      )}
    </>
  );
}

function Transactions() {
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const openProof = async transaction => {
    setLoading(true); setError("");
    try {
      setSelected(await adminFluxopayApi.transaction(transaction.transactionId));
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "N\u00e3o foi poss\u00edvel carregar a comprova\u00e7\u00e3o da transa\u00e7\u00e3o.");
    } finally { setLoading(false); }
  };

  return (
    <>
    <section className="afxp-card afxp-table-card">
      <header>
        <div>
          <small>OPERAÇÕES</small>
          <h3>Transações consolidadas</h3>
        </div>
        <button className="afxp-secondary">
          <FontAwesomeIcon icon={faDownload} />
          Exportar
        </button>
      </header>
      <div className="afxp-filters">
        <label>
          <FontAwesomeIcon icon={faFilter} />
          <input placeholder="Código, clínica ou paciente" />
        </label>
        <input type="date" defaultValue="2026-08-01" />
        <input type="date" defaultValue="2026-08-07" />
      </div>
      <div className="afxp-table-wrap">
        <table>
          <thead>
            <tr>
              <th>Transação</th>
              <th>Clínica / paciente</th>
              <th>Meio</th>
              <th>Bruto</th>
              <th>Provedor</th>
              <th>Fluxopay</th>
              <th>Líquido clínica</th>
              <th>Status</th>
              <th aria-label={"A\u00e7\u00f5es"}></th>
            </tr>
          </thead>
          <tbody>
            {TRANSACTIONS.map((t) => (
              <tr key={t.id}>
                <td>
                  <b>{t.id}</b>
                  <small>{t.date}</small>
                </td>
                <td>
                  <b>{t.clinic}</b>
                  <small>{t.patient}</small>
                </td>
                <td>{t.method}</td>
                <td>{brl(t.gross)}</td>
                <td className="afxp-red">− {brl(t.providerFee)}</td>
                <td className="afxp-green">+ {brl(t.platformFee)}</td>
                <td>
                  <b>{brl(t.net)}</b>
                </td>
                <td>
                  <Badge status={t.status} />
                </td>
                <td>
                  <button type="button" className="afxp-secondary afxp-detail-button" onClick={() => openProof(t)} aria-label={"Abrir comprova\u00e7\u00e3o da transa\u00e7\u00e3o"} title={"Comprova\u00e7\u00e3o da transa\u00e7\u00e3o"}>
                    <FontAwesomeIcon icon={faShieldHalved} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
    {error && <p className="afxp-page-error">{error}</p>}
    {loading && <div className="afxp-modal-backdrop"><section className="afxp-modal afxp-loading-modal">{"Carregando comprova\u00e7\u00e3o..."}</section></div>}
    {selected && <TransactionProofModal data={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function Fees({ onChanged, setError }) {
  const pixRule = FEE_RULES.find((rule) => rule.payment_method === "pix");
  const cardRule = FEE_RULES.find(
    (rule) =>
      rule.payment_method === "credit_card" &&
      Number(rule.installments_from) === 1 &&
      Number(rule.installments_to) === 1,
  );
  const installmentRule = FEE_RULES.find(
    (rule) =>
      rule.payment_method === "credit_card" &&
      Number(rule.installments_from) === 2 &&
      Number(rule.installments_to) === 12,
  );
  const [editing, setEditing] = useState(false);
  const [pixFee, setPixFee] = useState(
    String(pixRule?.platform_fee_percentage || "0"),
  );
  const [cardFee, setCardFee] = useState(
    String(cardRule?.platform_fee_percentage || "0"),
  );
  const [installmentFee, setInstallmentFee] = useState(
    String(installmentRule?.platform_fee_percentage || "0"),
  );
  const [saving, setSaving] = useState(false);
  const payload = (rule, percentage) => ({
    entidade_id: rule?.entidade_id || null,
    provider: rule?.provider || "asaas",
    payment_method: rule?.payment_method,
    installments_from: Number(rule?.installments_from || 1),
    installments_to: Number(rule?.installments_to || 1),
    platform_fee_type: rule?.platform_fee_type || "percentage",
    platform_fee_percentage: Number(percentage),
    platform_fee_fixed_cents: Number(rule?.platform_fee_fixed_cents || 0),
    provider_fee_percentage: Number(rule?.provider_fee_percentage || 0),
    provider_fee_fixed_cents: Number(rule?.provider_fee_fixed_cents || 0),
  });
  const save = async () => {
    if (!pixRule || !cardRule || !installmentRule) {
      setError(
        "As regras ativas de Pix, cartao 1x e cartao 2-12x precisam existir antes da edicao.",
      );
      return;
    }
    setSaving(true);
    try {
      await adminFluxopayApi.createFeeRule(payload(pixRule, pixFee));
      await adminFluxopayApi.createFeeRule(payload(cardRule, cardFee));
      await adminFluxopayApi.createFeeRule(
        payload(installmentRule, installmentFee),
      );
      setEditing(false);
      await onChanged();
    } catch (e) {
      setError(
        e.response?.data?.message || "Nao foi possivel salvar as taxas.",
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="afxp-grid afxp-grid--fees">
      <section className="afxp-card">
        <header>
          <div>
            <small>REGRAS COMERCIAIS</small>
            <h3>Taxas Fluxopay</h3>
          </div>
          <button
            className="afxp-secondary"
            onClick={() => setEditing(!editing)}
          >
            <FontAwesomeIcon icon={faGear} />
            {editing ? "Cancelar" : "Editar regras"}
          </button>
        </header>
        <p className="afxp-explanation">
          Valores cobrados pela plataforma e destinados à conta Fluxopay no
          split. Alterações criam uma nova versão e afetam somente cobranças
          futuras.
        </p>
        {FEE_RULES.map((rule) => [
          rule.payment_method === "pix"
            ? "Pix"
            : "Cartao " +
              rule.installments_from +
              "-" +
              rule.installments_to +
              "x",
          rule.platform_fee_percentage,
          "Percentual",
          rule,
        ]).map(([name, value, type, rule], i) => (
          <div className="afxp-fee-row" key={rule.id}>
            <div>
              <b>{name}</b>
              <small>{type} sobre o valor bruto</small>
            </div>
            {editing ? (
              <label>
                <input
                  type="number"
                  step="0.01"
                  value={
                    rule.payment_method === "pix"
                      ? pixFee
                      : Number(rule.installments_from) === 1
                        ? cardFee
                        : installmentFee
                  }
                  onChange={(e) =>
                    rule.payment_method === "pix"
                      ? setPixFee(e.target.value)
                      : Number(rule.installments_from) === 1
                        ? setCardFee(e.target.value)
                        : setInstallmentFee(e.target.value)
                  }
                />
                <span>%</span>
              </label>
            ) : (
              <strong>{pct(value)}</strong>
            )}
            <span className="afxp-version">
              Versão {rule.version} ·{" "}
              {new Date(rule.effective_from).toLocaleDateString("pt-BR")}
            </span>
          </div>
        ))}
        {editing && (
          <button className="afxp-primary" onClick={save} disabled={saving}>
            {saving ? "Salvando..." : "Salvar nova versão"}
          </button>
        )}
      </section>
      <AdminFluxopayProviderFees />
    </div>
  );
}

function Reconciliation({ onChanged, setError }) {
  const [running, setRunning] = useState(false);
  const [selected, setSelected] = useState(null);
  const execute = async () => {
    setRunning(true);
    try {
      await adminFluxopayApi.reconcileAsaas({
        reference_date: new Date().toISOString().slice(0, 10),
      });
      await onChanged();
    } catch (e) {
      setError(
        e.response?.data?.message ||
          "Não foi possível executar a conciliação Asaas.",
      );
    } finally {
      setRunning(false);
    }
  };
  const openDetails = async (row) => {
    try {
      setSelected(await adminFluxopayApi.reconciliation(row.id));
    } catch (e) {
      setError(
        e.response?.data?.message || "Não foi possível detalhar a conciliação.",
      );
    }
  };
  return (
    <>
      <section className="afxp-stats afxp-stats--three">
        <Stat
          icon={faCheckCircle}
          label="Conciliado no período"
          value={brl(
            RECONCILIATIONS.filter((r) => r.status === "matched").reduce(
              (sum, r) => sum + r.internal,
              0,
            ),
          )}
          detail={
            RECONCILIATIONS.filter((r) => r.status === "matched").length +
            " execucoes conciliadas"
          }
          tone="green"
        />
        <Stat
          icon={faScaleBalanced}
          label="Diferenças abertas"
          value={brl(
            RECONCILIATIONS.reduce((sum, r) => sum + Math.abs(r.difference), 0),
          )}
          detail={
            RECONCILIATIONS.filter((r) => r.status === "divergent").length +
            " divergencias"
          }
          tone="red"
        />
        <Stat
          icon={faClockRotateLeft}
          label="Última sincronização"
          value={RECONCILIATIONS[0]?.date || "Nunca"}
          detail={`${RECONCILIATIONS[0]?.provider || "asaas"} · dados do provedor`}
          tone="blue"
        />
      </section>
      <section className="afxp-card afxp-table-card">
        <header>
          <div>
            <small>CONCILIAÇÃO DIÁRIA</small>
            <h3>Fluxopay × provedor</h3>
          </div>
          <button
            className="afxp-secondary"
            onClick={execute}
            disabled={running}
          >
            <FontAwesomeIcon icon={faRotate} />
            {running ? "Executando..." : "Executar agora"}
          </button>
        </header>
        <div className="afxp-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Provedor</th>
                <th>Registros Fluxopay</th>
                <th>Extrato do provedor</th>
                <th>Diferença</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {RECONCILIATIONS.map((r) => (
                <tr key={r.id}>
                  <td>
                    <b>{r.date}</b>
                  </td>
                  <td>
                    <span className="afxp-provider">{r.provider}</span>
                  </td>
                  <td>{brl(r.internal)}</td>
                  <td>{brl(r.providerAmount)}</td>
                  <td className={r.difference ? "afxp-red" : "afxp-green"}>
                    <b>{brl(r.difference)}</b>
                  </td>
                  <td>
                    <Badge status={r.status} />
                    <small>{r.details}</small>
                  </td>
                  <td>
                    <button
                      className="afxp-secondary afxp-detail-button"
                      onClick={() => openDetails(r)}
                      aria-label="Detalhar conciliacao"
                    >
                      <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {selected && (
        <ReconciliationDetailsModal
          data={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

export default function AdminFluxopayPage() {
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [version, setVersion] = useState(0);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [accounts, transactions, feeRules, reconciliations] =
        await Promise.all([
          adminFluxopayApi.accounts({ per_page: 100 }),
          adminFluxopayApi.transactions({ per_page: 100 }),
          adminFluxopayApi.feeRules({ active: true }),
          adminFluxopayApi.reconciliations(),
        ]);

      hydrateAdmin(
        accounts,
        transactions,
        feeRules,
        reconciliations
      );
      setVersion((value) => value + 1);
    } catch (e) {
      setError(
        e.response?.data?.message ||
          "Não foi possível carregar a operação Fluxopay.",
      );
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const tabs = [
    ["overview", "Visão geral"],
    ["clinics", "Clínicas"],
    ["transactions", "Transações"],
    ["disputes", "Contestações"],
    ["fees", "Taxas e regras"],
    ["reconciliation", "Conciliação"],
    ["payout-reviews", "Repasses em revisão"],
    ["operational-alerts", "Alertas"],
      ["webhook-health", "Webhooks"],
  ];
  const title = tabs.find((x) => x[0] === tab)?.[1];
  if (loading && version === 0)
    return (
      <main className="afxp-page">
        <section className="afxp-card">Carregando operação Fluxopay...</section>
      </main>
    );
  return (
    <main className="afxp-page">
      <header className="afxp-header">
        <div>
          <span>
            <FontAwesomeIcon icon={faWallet} /> OPERAÇÃO FLUXOPAY
          </span>
          <h1>{title}</h1>
          <p>
            Gestão consolidada de pagamentos, subcontas, margem e liquidações.
          </p>
        </div>
        <div>
          <button className="afxp-icon" onClick={load} disabled={loading}>
            <FontAwesomeIcon icon={faRotate} />
          </button>
          <button className="afxp-primary">
            <FontAwesomeIcon icon={faCalendarDays} />
            {new Date().toLocaleDateString("pt-BR")}
          </button>
        </div>
      </header>
      {error && <p className="afxp-warning">{error}</p>}
      <nav className="afxp-tabs">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            className={tab === key ? "active" : ""}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </nav>
      <div className="afxp-content">
        {tab === "overview" && (<AdminFluxopayRealOverviewV2 setTab={setTab}/>)}{" "}
        {tab === "clinics" && <Clinics onChanged={load} setError={setError} />}{" "}
        {tab === "transactions" && <Transactions />}{" "}
        {tab === "disputes" && (<FluxopayDisputesPanel admin />)}{" "}
        {tab === "fees" && <Fees onChanged={load} setError={setError} />}{" "}
        {tab === "reconciliation" && (
          <Reconciliation onChanged={load} setError={setError} />
        )}
        {tab === "payout-reviews" && (<AdminFluxopayPayoutReviews />)}
        {tab === "operational-alerts" && (<AdminFluxopayOperationalAlerts />)}
        {tab === "webhook-health" && (<AdminFluxopayWebhookHealth />)}
      </div>
      <div className="afxp-demo">
        <FontAwesomeIcon icon={faShieldHalved} /> Dados consolidados do backend
        · provider Asaas
      </div>
    </main>
  );
}
